import React, { useEffect, useRef, useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  ZoomControl,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { Badge } from "./ui/badge";
import { Text } from "./catalyst/text";

// Icon fix - use CDN icons to avoid missing files
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

// Helper function to parse location (same as MapSection)
const parseLocation = (loc) => {
  const fallbackCenter = [39.8283, -98.5795];
  
  // Handle coordinate object format { lat: number, lng: number }
  if (loc && typeof loc === "object" && "lat" in loc && "lng" in loc) {
    const lat = parseFloat(loc.lat);
    const lng = parseFloat(loc.lng);
    if (!isNaN(lat) && !isNaN(lng)) {
      return [lat, lng];
    }
  }
  
  // Handle coordinate string format "(lat,lng)" or "lat,lng"
  if (loc && typeof loc === "string") {
    try {
      const clean = loc.replace(/[()]/g, "");
      const parts = clean.split(",");
      if (parts.length === 2) {
        const lat = parseFloat(parts[0].trim());
        const lng = parseFloat(parts[1].trim());
        if (!isNaN(lat) && !isNaN(lng)) {
          // Validate coordinates are in reasonable ranges
          if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            console.warn('Coordinates out of valid range:', { lat, lng });
          }
          
          return [lat, lng];
        }
      }
    } catch (error) {
      console.warn("Could not parse location coordinates:", loc);
    }
  }
  
  return fallbackCenter;
};

// MapBounds Component for fitting all markers
const MapBounds = ({ allCustomers }) => {
  const map = useMap();
  useEffect(() => {
    if (allCustomers && allCustomers.length > 0 && map) {
      try {
        if (!map.getContainer() || !map.getContainer()._leaflet_id) {
          return;
        }
        
        const bounds = L.latLngBounds([]);
        let validLocationsCount = 0;
        allCustomers.forEach((customer) => {
          if (customer?.location) {
            const [lat, lng] = parseLocation(customer.location);
            // Only include if coordinates are valid and not the fallback center
            const isValidCoords = isFinite(lat) && isFinite(lng) && 
                                 (lat !== 39.8283 || lng !== -98.5795);
            if (isValidCoords) {
              bounds.extend([lat, lng]);
              validLocationsCount++;
            }
          }
        });
        if (validLocationsCount > 0) {
          map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12 });
        }
      } catch (error) {
        console.warn("Map bounds warning:", error.message);
      }
    }
  }, [map, allCustomers]);
  return null;
};

// Cluster Marker Component - shows different colors for different schedules
const ClusterMarker = ({ position, customer, scheduleInfo }) => {
  // Generate a color based on schedule ID for visual distinction
  const getScheduleColor = (scheduleId) => {
    const colors = [
      '#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6',
      '#1abc9c', '#34495e', '#e67e22', '#95a5a6', '#f1c40f'
    ];
    const hash = scheduleId.toString().split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  const scheduleColor = getScheduleColor(scheduleInfo.scheduleId);
  
  const divIcon = L.divIcon({
    className: "custom-cluster-marker-icon",
    html: `<div class="flex items-center justify-center w-4 h-4 rounded-full text-white text-xs font-bold shadow-md border-2 border-white" style="background-color: ${scheduleColor}"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 16],
    popupAnchor: [0, -16],
  });

  return (
    <Marker position={position} icon={divIcon}>
      <Popup>
        <div className="p-1 space-y-1 text-sm max-w-[200px]">
          <p className="font-semibold truncate">{customer.customername || customer.name || "Unknown Customer"}</p>
          <Text className="text-xs truncate">{customer.formatted_address || customer.address || "No address"}</Text>
          <div className="flex flex-col gap-1 mt-2">
            <Text className="text-xs">
              <span className="font-medium">Schedule:</span> {scheduleInfo.scheduleName}
            </Text>
            <Text className="text-xs">
              <span className="font-medium">Region:</span> {scheduleInfo.region || "N/A"}
            </Text>
            {(customer.prioritybucket || customer.priority?.tier) && (
              <Badge variant="secondary" className="capitalize text-xs mt-1">
                {customer.prioritybucket || customer.priority.tier} Priority
              </Badge>
            )}
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

// Main AllSchedulesMapSection component
const AllSchedulesMapSection = ({ schedules }) => {
  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;
  const [mapKey, setMapKey] = useState(0);

  // Aggregate all customers from all schedules
  const allCustomers = useMemo(() => {
    if (!schedules || schedules.length === 0) return [];
    
    const customers = [];
    schedules.forEach(schedule => {
      if (schedule.itinerary?.customers) {
        schedule.itinerary.customers.forEach(customer => {
          customers.push({
            ...customer,
            scheduleInfo: {
              scheduleId: schedule.cluster,
              scheduleName: schedule.metadata?.itinerary_name || `Schedule ${schedule.cluster}`,
              region: schedule.metadata?.region
            }
          });
        });
      }
    });
    
    console.log(`AllSchedulesMapSection: Aggregated ${customers.length} customers from ${schedules.length} schedules`);
    return customers;
  }, [schedules]);

  // Error boundary to reset map on critical errors
  useEffect(() => {
    const handleError = (event) => {
      if (event.error && event.error.message && event.error.message.includes('_leaflet_pos')) {
        console.warn('Leaflet error detected, resetting map...');
        setMapKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  return (
    <div className="h-full w-full relative bg-muted/10">
      <MapContainer
        key={mapKey}
        center={defaultCenter}
        zoom={defaultZoom}
        style={{ width: "100%", height: "100%" }}
        scrollWheelZoom={true}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoomControl position="bottomright" />
        <MapBounds allCustomers={allCustomers} />

        {useMemo(() => 
          allCustomers?.map((customer, idx) => {
            if (!customer?.location) return null;
            const [lat, lng] = parseLocation(customer.location);
            // Only render marker if coordinates are valid and not the fallback center
            const isValidCoords = isFinite(lat) && isFinite(lng) && 
                                 (lat !== 39.8283 || lng !== -98.5795);
            if (!isValidCoords) return null;
            
            return (
              <ClusterMarker
                key={`all-marker-${customer.scheduleInfo.scheduleId}-${idx}`}
                position={[lat, lng]}
                customer={customer}
                scheduleInfo={customer.scheduleInfo}
              />
            );
          }).filter(Boolean), [allCustomers]
        )}
      </MapContainer>
    </div>
  );
};

export default AllSchedulesMapSection;